import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link2, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Skel } from "@/components/app/Common";
import { api, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";
import { showLinks } from "@/pages/assessments/AssessmentLinksDialog";

const MODES = [["email", "Send by email", Mail], ["link", "Copy link", Link2]];

export function SendAssessmentDialog({ jobId, applicationIds, open, onClose, onSent }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("email");
  const { data, isLoading, error } = useQuery({
    queryKey: ["assess-preview", jobId, applicationIds.join(",")], enabled: open && applicationIds.length > 0,
    queryFn: () => api.post(`/jobs/${jobId}/assessments/preview`, { application_ids: applicationIds }).then((r) => r.data),
  });
  const sendable = (data?.recipients || []).filter((r) => !r.already_invited);
  const refreshAll = () => ["ranked", "job-assessments", "assessments", "pipeline"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
  const makeLinks = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/jobs/${jobId}/assessments/links`, { application_ids: data.recipients.map((x) => x.application_id) });
      if (!r.links.length) toast.error(r.skipped.map((s) => `${s.name}: ${s.reason}`).join(" · ") || "No links created");
      else { showLinks(r); refreshAll(); onSent?.(); onClose(); }
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const confirm = async () => {
    if (mode === "link") return makeLinks();
    setBusy(true);
    try {
      const { data: r } = await api.post(`/jobs/${jobId}/assessments/send`, { application_ids: sendable.map((x) => x.application_id) });
      toast.success(`${r.sent} invite(s) sent${r.skipped ? ` · ${r.skipped} already invited` : ""}`);
      refreshAll(); onSent?.(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <>
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="send-assessment-dialog">
        <DialogHeader>
          <DialogTitle>Send assessment</DialogTitle>
          <DialogDescription>{data ? `${data.instrument} · ${data.expiry_days ? `link expires after ${data.expiry_days} days · reminders 48h and 24h before expiry` : "Link doesn't expire · single use"}` : "Preparing preview…"}</DialogDescription>
        </DialogHeader>
        {isLoading ? <Skel className="h-60" /> : error ? <p className="text-sm text-risk">{errMsg(error)}</p> : data && (
          <div className="space-y-4">
            <div className="flex w-fit rounded-[5px] border border-line bg-white p-0.5" role="radiogroup" aria-label="How to deliver the invite">
              {MODES.map(([k, l, Icon]) => (
                <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)} data-testid={`send-mode-${k}`}
                  className={cn("inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors duration-150", mode === k ? "bg-brand-tint font-medium text-brand" : "text-ink-secondary hover:text-ink")}>
                  <Icon size={14} aria-hidden="true" /> {l}</button>
              ))}
            </div>
            {data.blocked_reason && <p className="rounded-lg border border-[#E8D7B0] bg-caution-tint px-3 py-2 text-sm text-caution" data-testid="send-blocked-reason">{data.blocked_reason}</p>}
            <div>
              <p className="mb-2 text-sm font-medium">Recipients ({sendable.length} of {data.recipients.length})</p>
              <ul className="max-h-40 divide-y divide-line overflow-y-auto rounded-lg border border-line text-sm" data-testid="send-recipient-list">
                {data.recipients.map((r) => (
                  <li key={r.application_id} className="flex items-center justify-between px-3 py-2" data-testid={`send-recipient-${r.application_id}`}>
                    <span>{r.name}</span>
                    <span className="text-xs text-ink-muted">{mode === "link" ? (r.already_invited ? "Existing invite: link reused if available" : "New single-use link") : r.already_invited ? "Already invited — skipped" : r.has_email ? "Will receive email" : "No email on file"}</span>
                  </li>
                ))}
              </ul>
            </div>
            {mode === "email" ? (
              <div>
                <p className="mb-2 flex items-center gap-2 text-sm font-medium"><Mail size={14} /> {data.subject}</p>
                <iframe title="Email preview" sandbox="" srcDoc={data.html} className="h-64 w-full rounded-lg border border-line bg-white" data-testid="send-email-preview" />
              </div>
            ) : (
              <p className="rounded-[6px] bg-subtle px-3 py-3 text-sm text-ink-secondary" data-testid="send-link-explainer">No email is sent and no reminders are scheduled. You'll get a single-use link per candidate to share yourself. {data.expiry_days ? `Links expire after ${data.expiry_days} days.` : "Links don't expire · single use."}</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="send-cancel-btn">Cancel</Button>
          <Button onClick={confirm} disabled={busy || !data || !!data.blocked_reason || !(mode === "link" ? data.recipients.length : sendable.length)} data-testid="send-confirm-btn">
            {busy && <Loader2 size={14} className="animate-spin" />} {mode === "link" ? `Create ${data?.recipients.length || 0} link(s)` : `Send ${sendable.length} invite(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
